import { AlertTriangle, Check, Copy, KeyRound, Loader2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useCreatePersonalToken } from "@/lib/api";

export function PersonalTokenForm() {
  // Hold the freshly minted token in local state only — never persisted, never
  // logged. It's shown once; a page reload or navigation clears it for good.
  const [token, setToken] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<"command" | "token" | null>(null);
  const createMutation = useCreatePersonalToken();

  // The one-step setup command teammates paste into the Claude Code terminal:
  // writes the token to `.espresso-token` (no trailing newline) so the skill
  // picks it up. `printf` keeps it POSIX (macOS/Linux and the web workspace).
  const setupCommand = token ? `printf '%s' '${token}' > .espresso-token` : "";

  const handleGenerate = async () => {
    setCopiedKey(null);
    const result = await createMutation.mutateAsync();
    setToken(result?.token ?? null);
  };

  const handleCopy = async (value: string, key: "command" | "token") => {
    await navigator.clipboard.writeText(value);
    setCopiedKey(key);
    window.setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-4 w-4" />
          Personal access token
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground text-sm">
          This token lets the <code className="text-foreground">/weekly-status-capture</code> skill
          publish status updates on your behalf without logging in each time.
        </p>

        {token ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="tokenSetupCommand" className="text-foreground text-sm font-medium">
                One-step setup
              </label>
              <div className="flex gap-2">
                <Input
                  id="tokenSetupCommand"
                  readOnly
                  value={setupCommand}
                  className="font-mono text-xs"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => void handleCopy(setupCommand, "command")}
                >
                  {copiedKey === "command" ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copiedKey === "command" ? "Copied" : "Copy command"}
                </Button>
              </div>
              <p className="text-muted-foreground text-xs">
                Paste this into the <span className="text-foreground">Claude Code terminal</span>{" "}
                (from the project) and run it — it saves your token so the{" "}
                <code className="text-foreground">/weekly-status-capture</code> skill picks it up.
                Then re-run the skill.
              </p>
            </div>

            <details className="text-xs">
              <summary className="text-muted-foreground cursor-pointer">
                Prefer to place it yourself? Show the raw token
              </summary>
              <div className="mt-2 flex gap-2">
                <Input id="personalToken" readOnly value={token} className="font-mono text-xs" />
                <Button
                  type="button"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => void handleCopy(token, "token")}
                >
                  {copiedKey === "token" ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copiedKey === "token" ? "Copied" : "Copy"}
                </Button>
              </div>
              <p className="text-muted-foreground mt-1">
                Put it in a <code className="text-foreground">.espresso-token</code> file in the
                project root, or an <code className="text-foreground">ESPRESSO_API_TOKEN</code>{" "}
                environment variable.
              </p>
            </details>
          </div>
        ) : null}

        <div className="border-rag-red bg-rag-red/5 flex items-start gap-2 rounded-md border border-l-4 p-3">
          <AlertTriangle className="text-rag-red mt-0.5 h-4 w-4 shrink-0" />
          <p className="text-muted-foreground text-xs leading-relaxed">
            Treat this token like a password and store it safely. It is valid for a year. Generating
            a new one does not revoke old ones, and anyone who has it can act as you.
          </p>
        </div>

        <Button
          type="button"
          onClick={() => void handleGenerate()}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : token ? (
            "Generate a new token"
          ) : (
            "Generate token"
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
